import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

interface CustomerBalanceData {
  customer: {
    id: number;
    company_name: string;
    contact_name: string;
    email: string;
    phone: string;
    address: string;
  };
  balance: {
    total_invoiced: number;
    total_paid: number;
    balance_due: number;
    credit_balance: number;
    last_payment_date: string;
    last_invoice_date: string;
  };
  outstanding_invoices: Array<{
    id: number;
    invoice_number: string;
    order_number: string;
    total_amount: number;
    amount_paid: number;
    balance_due: number;
    due_date: string;
    status: string;
  }>;
  recent_payments: Array<{
    id: number;
    payment_number: string;
    amount: number;
    payment_method: string;
    payment_date: string;
    invoice_number?: string;
    status: string;
  }>;
}

const CustomerBalance: React.FC = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [data, setData] = useState<CustomerBalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isAuthenticated && !authLoading && customerId) {
      fetchCustomerBalance();
    }
  }, [isAuthenticated, authLoading, customerId]);

  const fetchCustomerBalance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/payments/customer/${customerId}/balance`);
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching customer balance:', error);
      setError(error.response?.data?.message || 'Failed to load customer balance');
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'paid': return 'success';
      case 'partially_paid': return 'warning';
      case 'overdue': return 'error';
      case 'unpaid': return 'default';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatPaymentMethod = (method: string) => {
    return method.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (authLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Please log in to view customer balance.</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Customer not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer-balances')}
          variant="outlined"
        >
          Back to Customer Balances
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Customer Account Balance
        </Typography>
      </Box>

      {/* Customer Info */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {data.customer.company_name}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Contact: {data.customer.contact_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: {data.customer.email}
            </Typography>
            {data.customer.phone && (
              <Typography variant="body2" color="text.secondary">
                Phone: {data.customer.phone}
              </Typography>
            )}
          </Box>
          {hasPermission('payments.create') && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/payments/new?customerId=${customerId}`)}
          >
            Record Payment
          </Button>
          )}
        </Box>
      </Paper>

      {/* Balance Overview */}
      {hasPermission('payments.read') && (
      <Box sx={{ mb: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'primary.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {formatCurrency(data.balance.total_invoiced)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Invoiced
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDownIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatCurrency(data.balance.total_paid)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Paid
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: data.balance.balance_due > 0 ? 'warning.main' : 'success.main'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{
                fontSize: 40,
                color: data.balance.balance_due > 0 ? 'warning.main' : 'success.main',
                mb: 1
              }} />
              <Typography
                variant="h4"
                fontWeight="bold"
                color={data.balance.balance_due > 0 ? 'warning.main' : 'success.main'}
              >
                {formatCurrency(data.balance.balance_due)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance Due
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: data.balance.credit_balance > 0 ? 'info.main' : 'grey.300'
          }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PaymentIcon sx={{
                fontSize: 40,
                color: data.balance.credit_balance > 0 ? 'info.main' : 'grey.500',
                mb: 1
              }} />
              <Typography
                variant="h4"
                fontWeight="bold"
                color={data.balance.credit_balance > 0 ? 'info.main' : 'text.secondary'}
              >
                {formatCurrency(data.balance.credit_balance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Credit Balance
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* Outstanding Invoices */}
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">
                Outstanding Invoices ({data.outstanding_invoices.length})
              </Typography>
            </Box>
            
            {data.outstanding_invoices.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No outstanding invoices
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell>Balance</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.outstanding_invoices.map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ReceiptIcon color="primary" fontSize="small" />
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.invoice_number}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(invoice.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            {formatCurrency(invoice.amount_paid)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            color={invoice.balance_due > 0 ? 'warning.main' : 'success.main'}
                          >
                            {formatCurrency(invoice.balance_due)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(invoice.due_date)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            color={getStatusColor(invoice.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/invoices/${invoice.id}`)}
                            sx={{ color: 'primary.main' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>

        {/* Recent Payments */}
        {hasPermission('payments.read') && (
        <Box sx={{ flex: '0 0 400px', minWidth: 0 }}>
          <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">
                Recent Payments ({data.recent_payments.length})
              </Typography>
            </Box>

            {data.recent_payments.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No recent payments
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {data.recent_payments.map((payment, index) => (
                  <Box key={payment.id}>
                    <Box sx={{ p: 3 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {payment.payment_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(payment.payment_date)}
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          {formatCurrency(payment.amount)}
                        </Typography>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Chip
                            label={formatPaymentMethod(payment.payment_method)}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={payment.status}
                            color={getPaymentStatusColor(payment.status)}
                            size="small"
                          />
                        </Box>
                        {payment.invoice_number && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.invoice_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {index < data.recent_payments.length - 1 && <Divider />}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Box>
        )}
      </Box>
    </Box>
  );
};

export default CustomerBalance;